import { tableBuilderActions } from '@constants/actionTypes';
import { useAdvancedSettings } from '@hooks/application/useAdvancedSettings';
import { runStream } from '@hooks/features/useStreamingResult';
import { submitQueriesStream } from '@services/genaiQueriesV2';
import { BASEURL } from '@services/network';
import { logger } from '@utils/usefulFuncs';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

const ChatWindowWrapper = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;

  height: 100px;
  width: 100%;

  background-color: ${({ theme }) => theme.themeColors.componentNoOpacity};
  border: 1px solid ${({ theme }) => theme.themeColors.border};

  z-index: 1000;
  border-radius: 2px;
`;
const ChatWindowBody = styled.div`
  padding: 8px;
`;
const InputField = styled.textarea`
  font-size: 14px;
  width: 100%;
  height: 80px;
  border: none;
  //   border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 5px;
  padding: 10px;
  resize: none;
  outline: none;
`;
const ActionsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
`;

const SendIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
    <path d="M13,20H11V8L5.5,13.5L4.08,12.08L12,4.16L19.92,12.08L18.5,13.5L13,8V20Z" />
  </svg>
);

const SendButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.themeColors.primary};
  color: ${({ theme }) => theme.themeColors.componentNoOpacity};
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  margin-left: 8px;
  margin-right: 8px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.themeColors.primaryAccent};
  }

  svg {
    color: ${({ theme }) => theme.themeColors.background};
    height: 22px;
    width: 22px;
  }
`;

function formatAgentOutput(output) {
  try {
    if (typeof output === 'string') {
      // Extract only the content inside the first json code block (```json ... ```)
      const codeBlockRegex = /```(?:json)?\s*([\s\S]*?)\s*```/im;
      const match = output.match(codeBlockRegex);
      if (match) {
        output = match[1];
      } else {
        // If no code block, remove all text before and after the first valid JSON object/array
        const jsonMatch = output.match(/(\{[\s\S]*\}|\[[\s\S]*\])/m);
        if (jsonMatch) {
          output = jsonMatch[0];
        }
      }
    }
    return JSON.parse(output);
  } catch (error) {
    console.log('error formatting agent output', { error, output });
    return output;
  }
}

const initAgentStream = async (resultId, dispatch) => {
  if (!resultId) return;

  const URL = `${BASEURL}/api/v2/genai/queries/stream_events?run_id=${encodeURIComponent(resultId)}&include_agent_plan=true`;
  let prevMessage = null;
  const handlers = {
    onMessage: (data) => {
      const result = JSON.parse(data);
      logger('tableBuilder ChatWindow onMessage', 'received', {
        runId: resultId,
        questionIndex: result?.question_index,
        finalAnswer: result?.final_answer === true,
        answerLength: result?.answer?.length || 0,
      });
      if (result.answer !== prevMessage) {
        prevMessage = result.answer;
      }
    },
    onError: () => {
      dispatch({
        type: tableBuilderActions.updateChatError,
        payload: 'Something went wrong, try again.',
      });
    },
    onComplete: (data) => {
      try {
        const response = JSON.parse(data.data).response;
        const answers = response?.answers || [];
        const row = answers.find((r) => r?.final_answer) || answers[0];
        if (!row) throw new Error('No answers in response');
        const finalOutput = formatAgentOutput(row.answer);
        dispatch({ type: tableBuilderActions.setWorkingState, payload: 'complete' });
        dispatch({ type: tableBuilderActions.setCurrentTableJSON, payload: finalOutput });
      } catch (error) {
        console.error('error parsing complete response', { error });
        dispatch({ type: tableBuilderActions.setWorkingState, payload: 'errored' });
        dispatch({ type: tableBuilderActions.updateChatError, payload: { error: 'Something went wrong, try again.' } });
      }
    },
  };
  runStream(URL, handlers);
};

const generatePrompt = (question, currentTableJSON) => {
  let prompt = `You are a financial analyst building a table. The response must be a json code block with table result, do not include any other text in the response. A cell should have the following keys: rowIndex, columnIndex, value. A cell can also have extra styling keys: bold, italic, underline, strike, backgroundColor, textColor. Add any header labels or row labels when appropriate. Label cells should be bold.`;
  if (!currentTableJSON) {
    prompt += `\n\nThe table should be built based on the following description: ${question}`;
  } else {
    prompt += `\n\nThe current table is: ${JSON.stringify(
      currentTableJSON
    )}. Update the table based on the following description: ${question}`;
  }
  return prompt;
};

export const ChatWindow = ({ tool, agentName }) => {
  const dispatch = useDispatch();
  const { currentTableJSON } = useSelector((state) => state.tableBuilder);
  const { model } = useAdvancedSettings();
  const [question, setQuestion] = React.useState('');

  const startStreamAnswer = (id, dispatch) => {
    initAgentStream(id, dispatch);
  };

  const onSubmit = async ({ question }) => {
    const finalQuestion = generatePrompt(question, currentTableJSON);
    const settings = { model, user_mode: tool };
    dispatch({ type: tableBuilderActions.addChatHistory, payload: { question } });
    dispatch({ type: tableBuilderActions.setWorkingState, payload: 'working' });
    const result = await submitQueriesStream({
      questions: finalQuestion,
      agentName,
      settings,
    });
    if (result.status === 200) {
      startStreamAnswer(result.runId, dispatch);
    } else {
      const message = result.error || 'Something went wrong, try again.';
      dispatch({ type: tableBuilderActions.updateChatError, payload: { error: message } });
      return { error: message };
    }
  };

  const handleSend = () => {
    onSubmit({ question });
    setQuestion('');
  };

  const handleInputChange = (e) => {
    setQuestion(e.target.value);
  };

  return (
    <ChatWindowWrapper>
      <ChatWindowBody>
        <ActionsWrapper>
          <InputField
            placeholder={
              currentTableJSON.length === 0
                ? 'Describe the table you want to create'
                : 'Describe a change to refine the table'
            }
            value={question}
            onChange={handleInputChange}
          />
          <SendButton onClick={handleSend}>
            <SendIcon />
          </SendButton>
        </ActionsWrapper>
      </ChatWindowBody>
    </ChatWindowWrapper>
  );
};
